import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import componentStyles from './idblab-button.css.lit';
import { OutlineButton } from '../../base/outline-button/outline-button';
import { LinkTargetType } from '../../base/outline-link/config';
import { classMap } from 'lit/directives/class-map.js';
import '../../idb/idb-icon/idb-icon';
import { SlotController } from '../../controllers/slot-controller';

export type ButtonVariant = 'none' | 'primary' | 'secondary';

export type ButtonSize = 'small' | 'medium' | 'large';

export type ButtonColors = 'magenta' | 'blue' | 'green';

/**
 * The IdbLabButton component
 * @element idblab-button
 * @slot idblab-button--content - content slot.
 */
@customElement('idblab-button')
export class IdbLabButton extends OutlineButton {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(
    this, // This, the host element.
    true // To shift or not to shift LightDom nodes to ShadowDOM.
  );

  @query('.btn')
  button: HTMLButtonElement;

  /**
   * The url to use for a link. This will render an anchor element.
   * Do not set this prop if you want to render a button element.
   */
  @property({ type: String, attribute: 'button-url' })
  buttonUrl: string;

  /**
   * ARIA label attribute to pass down to the resulting button or a
   * element. This is required for accessibility if we use a button
   * with an icon only.
   */
  @property({ type: String, attribute: 'button-label' })
  buttonLabel: string;

  /**
   * The target to use for a link, used in conjunction with the url attribute.
   */
  @property({ type: String, attribute: 'button-target' })
  buttonTarget: LinkTargetType = '_self';

  /**
   * The button style variant to use.
   */
  @property({ type: String, attribute: 'button-variant' })
  buttonVariant: ButtonVariant = 'primary';

  /**
   * The button size to use.
   */
  @property({ type: String, attribute: 'button-size' })
  buttonSize: ButtonSize = 'medium';

  /**
   * The (optional) icon to use on left side of button.
   */
  @property({ type: String, attribute: 'icon-left' })
  iconLeft: string | boolean = false;

  /**
   * The (optional) icon to use on right side of button.
   */
  @property({ type: String, attribute: 'icon-right' })
  iconRight: string | boolean = false;

  /**
   * Whether the button is disabled. Only applies to
   * implementations not using the url property
   */
  @property({ type: Boolean, attribute: 'is-disabled' })
  isDisabled = false;

  // Center the anchor text within the button
  @property({ type: Boolean, attribute: 'center-items' })
  centerItems = false;

  /**
   * Whether the button is disabled. Only applies to
   * implementations not using the url property
   */
  @property({ type: Boolean, attribute: 'icon-only' })
  iconOnly = false;

  /**
   * Whether the button is active. Applies the hover/focus state color to the button.
   */
  @property({ type: Boolean, attribute: 'active' })
  active = false;

  /**
   * A click handler to be passed only to onClick. DO NOT USE @click on this component.
   */
  @property() onClick: () => void;

  /**
   * A keyUp handler to be passed to the onKeyUp. DO NOT USE @keyup on this component.
   */
  @property() onKeyUp: () => void;

  /**
   * Button Color options
   */
  @property({ type: String, attribute: 'button-color' })
  buttonColor: ButtonColors;

  firstUpdated(): void {
    const link = this.querySelector('a');
    if (link) {
      this.button.setAttribute('tabindex', '-1');
      this.addEventListener('click', () => {
        link.click();
      });
    }
  }

  updated() {
    // checks the is-disabled prop and manages aria-disabled attributes on the <outline-button> element itself.
    if (this.hasAttribute('is-disabled')) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.setAttribute('aria-disabled', 'false');
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    const link = this.querySelector('a');
    if (link) {
      this.removeEventListener('click', () => {
        link.click();
      });
    }
  }

  /**
   * Component render function
   * @returns TemplateResult
   */
  render(): TemplateResult {
    const buttonClasses = {
      'btn': true,
      [this.buttonVariant]: true,
      [this.buttonSize]: true,
      'active': this.active,
      'grid-button-wrapper': this.centerItems,
      [`button-color--${this.buttonColor}`]: this.buttonColor !== undefined,
    };

    const iconOnlyClasses = {
      'icon-only': this.iconOnly,
      'grid-button': this.centerItems,
      ...buttonClasses,
    };

    return this.buttonUrl
      ? html` <a
          class="${classMap(iconOnlyClasses)}"
          href=${this.buttonUrl}
          target=${this.buttonTarget}
          aria-label="${this.buttonLabel}"
          aria-disabled=${this.isDisabled}
        >
          ${this.buttonTemplate()}
        </a>`
      : html`<button
          class="${classMap(buttonClasses)}"
          aria-disabled="${this.isDisabled}"
          aria-label="${this.buttonLabel}"
          .onclick="${this.onClick}"
          .onkeyup="${this.onKeyUp}"
        >
          ${this.buttonTemplate()}
        </button> `;
  }

  /**
   * Method to return the html required to render the button contents.
   *
   * @returns TemplateResult
   */
  buttonTemplate(): TemplateResult {
    return html`
        ${
          this.iconLeft
            ? html`<idblab-icon class="left" icon="${this.iconLeft}" />`
            : null
        }
        <slot></slot>
        ${
          this.iconRight
            ? html`<idblab-icon class="right" icon="${this.iconRight}" />`
            : null
        }
      `;
  }
}
